// src/hooks/useNetworkStatus.ts
// 網路狀態監聽 hook，用於 PWA 離線優先體驗

import { useState, useEffect, useCallback } from 'react';
import { logInfo } from '../utils/logger';

interface NetworkStatus {
  /** 是否在線 */
  isOnline: boolean;
  /** 是否從離線恢復（剛剛上線） */
  justReconnected: boolean;
  /** 上次連線時間 */
  lastOnlineAt: number | null;
}

/**
 * 監聽網路狀態的 Hook
 *
 * 特點：
 * - 防抖處理，避免網路狀態快速切換
 * - 追蹤「剛恢復連線」狀態，方便觸發同步
 * - 提供上次在線時間
 */
export const useNetworkStatus = (debounceMs = 1000): NetworkStatus => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [justReconnected, setJustReconnected] = useState(false);
  const [lastOnlineAt, setLastOnlineAt] = useState<number | null>(
    navigator.onLine ? Date.now() : null
  );

  useEffect(() => {
    let onlineTimeoutId: NodeJS.Timeout | null = null;
    let offlineTimeoutId: NodeJS.Timeout | null = null;
    let reconnectedTimeoutId: NodeJS.Timeout | null = null;

    const handleOnline = () => {
      // 清除離線延遲
      if (offlineTimeoutId) {
        clearTimeout(offlineTimeoutId);
        offlineTimeoutId = null;
      }

      // 延遲處理，防止網路狀態快速切換
      onlineTimeoutId = setTimeout(() => {
        setIsOnline(true);
        setLastOnlineAt(Date.now());
        setJustReconnected(true);
        logInfo('Network', '網路已連線');

        // 3 秒後清除「剛恢復連線」狀態
        reconnectedTimeoutId = setTimeout(() => {
          setJustReconnected(false);
        }, 3000);
      }, debounceMs);
    };

    const handleOffline = () => {
      // 清除上線延遲
      if (onlineTimeoutId) {
        clearTimeout(onlineTimeoutId);
        onlineTimeoutId = null;
      }

      // 延遲處理離線狀態
      offlineTimeoutId = setTimeout(() => {
        setIsOnline(false);
        setJustReconnected(false);
        logInfo('Network', '網路已斷線，切換到離線模式');
      }, debounceMs);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if (onlineTimeoutId) clearTimeout(onlineTimeoutId);
      if (offlineTimeoutId) clearTimeout(offlineTimeoutId);
      if (reconnectedTimeoutId) clearTimeout(reconnectedTimeoutId);
    };
  }, [debounceMs]);

  return { isOnline, justReconnected, lastOnlineAt };
};

/**
 * 簡化版：只返回是否在線
 */
export const useIsOnline = (): boolean => {
  const { isOnline } = useNetworkStatus();
  return isOnline;
};
